import { FieldWarning, money } from './inputSanityModel'
import { sumAdminEmployeeCost, sumFieldEmployeeCost } from './employeeCostModel'
import {
  AdminEmployeesFormData,
  CompanyFormData,
  FieldEmployeesFormData,
  HeavyMachineryFormData,
  OwnerFormData,
  TrailersFormData,
  TrucksFormData
} from './types'

export const ADMIN_WAGES_SHARE_CEILING = 0.3
export const EQUIPMENT_SHARE_CEILING = 0.2
export const EQUIPMENT_SHARE_TYPICAL_LOW = 3
export const EQUIPMENT_SHARE_TYPICAL_HIGH = 11
export const REVENUE_PER_ADMIN_FLOOR = 150000
export const REVENUE_PER_ADMIN_TYPICAL = 200000
export const MIN_ADMIN_HEADCOUNT = 2
export const ADMIN_OVER_FIELD_MARGIN = 2

export type OwnedOrLeased = {
  ownershipType: 'Own' | 'Lease/Finance'
  replacementCost: number | null
  sellPrice: number | null
  avgUsefulLife: number | null
  monthlyPayment: number | null
}

const percent = (share: number): string => `${Math.round(share * 100)}`

export const itemAnnualCost = (item: OwnedOrLeased): number => {
  if (item.ownershipType === 'Lease/Finance') {
    return (item.monthlyPayment ?? 0) * 12
  }
  const life = item.avgUsefulLife ?? 0
  if (life <= 0) {
    return 0
  }
  return Math.max(0, ((item.replacementCost ?? 0) - (item.sellPrice ?? 0)) / life)
}

export const sumItemAnnualCost = (items: OwnedOrLeased[]): number => {
  return items.reduce((total, item) => total + itemAnnualCost(item), 0)
}

const GOAL_COLLISION_COURSE =
  "Then that puts you on a collision course to pay out more than the year brings in revenue. It's usually an input somewhere with an incorrect number."

export const checkGoalVsPayroll = (
  salesGoal: number | null,
  ownerSalary: number | null,
  crewWages: number,
  adminWages: number
): FieldWarning | null => {
  const goal = salesGoal ?? 0
  const owner = ownerSalary ?? 0
  const payroll = owner + crewWages + adminWages
  if (goal <= 0 || payroll <= 0 || goal >= payroll) {
    return null
  }

  const coaching = {
    objection: 'But both of those are right',
    body: [
      GOAL_COLLISION_COURSE,
      "The estimated revenue input wants the whole year's sales. Not one job, not one month. And the pay inputs want what each person costs you for the year.",
      'Hit the Fix the revenue button to change your estimated revenue without leaving this step. The pay is right here.'
    ]
  }

  if (crewWages > goal) {
    return {
      field: 'goalVsPayroll',
      severity: 'peer',
      text: `You said your estimated revenue is ${money(goal)}, but you told us you pay your crew ${money(crewWages)}. Both of those can't be true at the same time — which one's wrong? Fix that one before you keep going.`,
      coaching
    }
  }

  const ownPay = crewWages > 0 ? "your own pay plus your crew's" : 'your own pay'
  return {
    field: 'goalVsPayroll',
    severity: 'peer',
    text: `You said your estimated revenue is ${money(goal)}, but ${ownPay} adds up to ${money(payroll)}. Both of those can't be true at the same time — which one's wrong? Fix that one before you keep going.`,
    coaching
  }
}

export const checkGoalVsCosts = (salesGoal: number | null, costs: number): FieldWarning | null => {
  const goal = salesGoal ?? 0
  if (goal <= 0 || costs <= 0 || goal >= costs) {
    return null
  }
  return {
    field: 'goalVsCosts',
    severity: 'peer',
    text: `You said your estimated revenue is ${money(goal)}, but the costs in your budget add up to ${money(costs)}. Both of those can't be true at the same time — which one's wrong? Fix the revenue, or these costs.`,
    coaching: {
      objection: 'But these numbers are right',
      body: [
        GOAL_COLLISION_COURSE,
        'These costs are the pay and the equipment you have entered so far. The pay inputs want what each person costs you for the year, and the equipment inputs want what each machine costs you to own.',
        'Hit the Fix the revenue button to change your estimated revenue without leaving this step. The costs are on the steps in between.'
      ]
    }
  }
}

export const checkAdminWagesShare = (
  salesGoal: number | null,
  adminWages: number
): FieldWarning | null => {
  const goal = salesGoal ?? 0
  if (goal <= 0 || adminWages <= 0) {
    return null
  }
  const share = adminWages / goal
  if (share <= ADMIN_WAGES_SHARE_CEILING) {
    return null
  }
  return {
    field: 'adminWagesShare',
    severity: 'peer',
    text: `You said your estimated revenue is ${money(goal)}, and the office pay you've entered adds up to ${money(adminWages)} — that's ${percent(share)}% of it. Most businesses your size land under ${percent(ADMIN_WAGES_SHARE_CEILING)}%. Check the pay, or the revenue.`
  }
}

export const checkAdminLoad = (
  salesGoal: number | null,
  adminCount: number,
  fieldCount: number
): FieldWarning | null => {
  const goal = salesGoal ?? 0
  if (adminCount < MIN_ADMIN_HEADCOUNT) {
    return null
  }

  if (adminCount >= fieldCount + ADMIN_OVER_FIELD_MARGIN) {
    const onTheTools = fieldCount === 0 ? 'nobody' : `${fieldCount}`
    return {
      field: 'adminLoad',
      severity: 'peer',
      text: `That's ${adminCount} office staff with ${onTheTools} on the tools. If some of these folks do field work, they belong on the crew step.`
    }
  }

  if (goal > 0 && goal / adminCount < REVENUE_PER_ADMIN_FLOOR) {
    return {
      field: 'adminLoad',
      severity: 'peer',
      text: `That's ${adminCount} office staff for a ${money(goal)} year. It usually takes about ${money(REVENUE_PER_ADMIN_TYPICAL)} of revenue to carry each office hire. If some of these folks work on the tools, they belong on the crew step.`
    }
  }

  return null
}

export const checkEquipmentShare = (
  salesGoal: number | null,
  equipmentCost: number
): FieldWarning | null => {
  const goal = salesGoal ?? 0
  if (goal <= 0 || equipmentCost <= 0) {
    return null
  }
  const share = equipmentCost / goal
  if (share <= EQUIPMENT_SHARE_CEILING) {
    return null
  }
  return {
    field: 'equipmentShare',
    severity: 'peer',
    text: `Your equipment comes to ${money(equipmentCost)} a year, and you said your estimated revenue is ${money(goal)} — so the gear eats ${percent(share)}% of it. Most businesses your size land between ${EQUIPMENT_SHARE_TYPICAL_LOW}% and ${EQUIPMENT_SHARE_TYPICAL_HIGH}%.`,
    coaching: {
      objection: "But that's what my equipment costs",
      body: [
        'It might — plenty of gear-heavy outfits run past that.',
        `Here's the thing — there are two ways to balance it. Either this year can produce more than ${money(goal)}, or the equipment is carrying too much of it.`,
        "If the gear's right, look at the revenue. If the revenue's right, trim the gear: sell what you're not running, or stretch the life — a truck run ten years instead of five costs half as much a year."
      ]
    }
  }
}

export type StepTotals = {
  salesGoal: number | null
  ownerSalary: number | null
  crewWages: number
  adminWages: number
  adminCount: number
  fieldCount: number
  equipmentCost: number
}

const firstOf = (...warnings: (FieldWarning | null)[]): FieldWarning | null => {
  return warnings.find(warning => warning !== null) ?? null
}

export const crewStepWarning = (totals: StepTotals): FieldWarning | null => {
  return checkGoalVsPayroll(totals.salesGoal, totals.ownerSalary, totals.crewWages, totals.adminWages)
}

export const adminStepWarning = (totals: StepTotals): FieldWarning | null => {
  return firstOf(
    checkGoalVsPayroll(totals.salesGoal, totals.ownerSalary, totals.crewWages, totals.adminWages),
    checkAdminWagesShare(totals.salesGoal, totals.adminWages),
    checkAdminLoad(totals.salesGoal, totals.adminCount, totals.fieldCount)
  )
}

export const equipmentStepWarning = (totals: StepTotals): FieldWarning | null => {
  const costs = (totals.ownerSalary ?? 0) + totals.crewWages + totals.adminWages + totals.equipmentCost
  return firstOf(
    checkGoalVsCosts(totals.salesGoal, costs),
    checkEquipmentShare(totals.salesGoal, totals.equipmentCost)
  )
}

export type BuilderTotalsInput = {
  companyData: CompanyFormData
  ownerData: OwnerFormData
  fieldEmployeesData: FieldEmployeesFormData
  adminEmployeesData: AdminEmployeesFormData
  trucksData: TrucksFormData
  trailersData: TrailersFormData
  heavyMachineryData: HeavyMachineryFormData
  workingWeeks: number
}

export const buildStepTotals = (input: BuilderTotalsInput): StepTotals => {
  const equipment: OwnedOrLeased[] = [
    ...input.trucksData.trucks,
    ...input.trailersData.trailers,
    ...input.heavyMachineryData.machinery
  ]
  return {
    salesGoal: input.companyData.salesGoal,
    ownerSalary: input.ownerData.annualSalary,
    crewWages: sumFieldEmployeeCost(input.fieldEmployeesData.employees, input.workingWeeks),
    adminWages: sumAdminEmployeeCost(input.adminEmployeesData.employees, input.workingWeeks),
    adminCount: input.adminEmployeesData.employees.length,
    fieldCount: input.fieldEmployeesData.employees.length,
    equipmentCost: sumItemAnnualCost(equipment)
  }
}
